import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EditOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import AuthService from "../../../base/services/authentication.service";
import { httpService } from "../../../base/services/httpService.service";
import { formatDate } from "../../../base/utils/dateFormat";
import { translateGenderName } from "../../../base/utils/genderLabels";
import { translateRoleName } from "../../auth/userRoles.enum";
import { useEnterWorkspaceForOthers } from "../../admin/workspace/components/utility/workspaceUtils";
import { useTranslation } from "../../../i18n/LanguageContext";
import "./components/organizationProfileSelf.scss";

// Personal profile for the Manager and Staff roles. They can't read the organization
// profile endpoints (403), so this only shows data their own account can load.
const ManagerProfile = () => {
   const { t } = useTranslation();
   const navigate = useNavigate();
   const enterWorkspace = useEnterWorkspaceForOthers();
   const user = AuthService.getUser();

   const [profile, setProfile] = useState(null);
   const [email, setEmail] = useState(user?.email || "");
   const [workspaces, setWorkspaces] = useState(null);

   useEffect(() => {
      httpService.get("/Accounts/ngo-member-profile", (res) => setProfile(res?.data)).catch(() => {});
      httpService
         .get("/Accounts/email", (res) => res?.data?.email && setEmail(res.data.email))
         .catch(() => {});
      httpService
         .get("/Workspaces/get-my-workspaces?pageSize=100&pageNumber=1", (res) =>
            setWorkspaces(Array.isArray(res?.data) ? res.data : [])
         )
         .catch(() => setWorkspaces([]));
   }, []);

   const noInfo = t("no-information-available-text");
   const fullName = profile?.name || user?.fullName || noInfo;
   const roleName = translateRoleName(AuthService.getActiveRoleName() || user?.roles?.[0]?.name, t);
   const genderName = profile?.gender?.name ? translateGenderName(profile.gender.name, t) : noInfo;
   const birthdate = profile?.birthday ? formatDate(profile.birthday) : noInfo;

   return (
      <div className="org-profile-page">
         <div className="org-profile-card">
            <div className="org-profile-header">
               <div className="org-profile-header-main">
                  <div className="org-profile-avatar">
                     <UserOutlined />
                  </div>
                  <div className="org-profile-header-text">
                     <h1 className="org-profile-name">{fullName}</h1>
                     <div className="org-profile-meta">
                        <div className="org-profile-status-badge">
                           <div className="org-profile-status-dot" />
                           <span>{roleName}</span>
                        </div>
                        {email && (
                           <>
                              <span className="org-profile-meta-text">⋅</span>
                              <span className="org-profile-meta-text">{email}</span>
                           </>
                        )}
                     </div>
                  </div>
               </div>
               <button
                  type="button"
                  className="org-profile-edit-btn"
                  onClick={() => navigate("/profile-information")}
               >
                  <EditOutlined />
                  {t("edit-action")}
               </button>
            </div>
            <div className="org-profile-stats">
               <div className="org-profile-stat">
                  <div className="org-profile-stat-label">{t("menu-item-workspaces")}</div>
                  <div className="org-profile-stat-value">
                     {workspaces === null ? "-" : workspaces.length}
                  </div>
               </div>
               <div className="org-profile-stat">
                  <div className="org-profile-stat-label">{t("role-label")}</div>
                  <div className="org-profile-stat-value">{roleName}</div>
               </div>
               <div className="org-profile-stat">
                  <div className="org-profile-stat-label">{t("gender-label")}</div>
                  <div className="org-profile-stat-value">{genderName}</div>
               </div>
            </div>
         </div>

         <div className="org-profile-middle-row">
            <div className="org-profile-middle-left">
               <div className="org-profile-card">
                  <div className="org-profile-section-header">
                     <div className="org-profile-section-title">
                        {t("personal-information-title")}
                     </div>
                  </div>
                  <div className="org-profile-section-body">
                     <div className="org-profile-field-pair">
                        <div>
                           <div className="org-profile-field-label">{t("full-name-label")}</div>
                           <div className="org-profile-field-value">{fullName}</div>
                        </div>
                        <div>
                           <div className="org-profile-field-label">{t("email-field-label")}</div>
                           <div className="org-profile-field-value">{email || noInfo}</div>
                        </div>
                     </div>
                     <div className="org-profile-field-pair">
                        <div>
                           <div className="org-profile-field-label">{t("gender-label")}</div>
                           <div className="org-profile-field-value">{genderName}</div>
                        </div>
                        <div>
                           <div className="org-profile-field-label">{t("birthdate-label")}</div>
                           <div className="org-profile-field-value">{birthdate}</div>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="org-profile-card">
                  <div className="org-profile-section-header">
                     <div className="org-profile-section-title">{t("my-workspaces-tab")}</div>
                  </div>
                  <div className="org-profile-section-body">
                     <div className="org-profile-tags">
                        {workspaces?.length ? (
                           workspaces.map((workspace) => (
                              <button
                                 type="button"
                                 className="org-profile-tag manager-profile-workspace-tag"
                                 key={workspace.id}
                                 onClick={() => enterWorkspace(workspace.id)}
                              >
                                 {workspace.name}
                              </button>
                           ))
                        ) : (
                           <div className="org-profile-description">
                              {workspaces === null ? "-" : t("no-workspaces-here-title")}
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </div>

            <div className="org-profile-middle-right org-profile-card">
               <div className="org-profile-section-header">
                  <div className="org-profile-section-title">{t("contact-label")}</div>
               </div>
               <div className="org-profile-section-body">
                  <div className="org-profile-contact-row">
                     <div className="org-profile-contact-icon">
                        <MailOutlined />
                     </div>
                     <div className="org-profile-contact-text">
                        <div className="org-profile-field-label">{t("email-field-label")}</div>
                        <div className="org-profile-field-value">{email || noInfo}</div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default ManagerProfile;
